import useIsMobile from 'utils/hooks/useIsMobile'
import useWindowWidth from 'utils/hooks/useWindowWidth'
import SquareIcon from '@mui/icons-material/Square'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {CircularProgress} from '@mui/material'
import _ from 'lodash'
import {Bar, Doughnut} from 'react-chartjs-2'
import {windowWidthResize} from 'utils/func/justUtils'

export interface ChartLegendItem {
  label: string
  count: any
  color: any
  onClick: () => void
}

interface StatusChartCardProps {
  chartType: 'doughnut' | 'bar'
  data: any
  options: any
  chartId: string
  legendId: string
  legend: ChartLegendItem[]
  cardPadding?: string
  legendFontClass?: string
  legendItemWidth?: number
  legendItemMarginLeft?: string
  loading?: boolean
  loadingGate?: boolean
  /* 모바일 타일 헤더용. 차트 옵션의 title 과 달리 총건수를 따로 표기한다. */
  title?: string
  total?: number
  /* 이동 권한이 없는 계정은 탭을 막고 수치만 보여준다. */
  interactive?: boolean
}

/**
 * 대시보드 도넛/막대 차트 + 우측 범례 리스트 공통 카드.
 * 데스크탑은 기존 차트 마크업 그대로, 모바일(<=991px)에서는 캔버스 대신
 * 제목/총건수/항목별 건수로 이루어진 KPI 타일로 렌더한다.
 */
const StatusChartCard = ({
  chartType,
  data,
  options,
  chartId,
  legendId,
  legend,
  cardPadding = 'p-0',
  legendFontClass = '',
  legendItemWidth = 300,
  legendItemMarginLeft,
  loading = true,
  loadingGate = false,
  title,
  total,
  interactive = true,
}: StatusChartCardProps) => {
  const windowWidth = useWindowWidth()
  const fontSize = windowWidthResize(windowWidth)
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <div className='border rounded-lg shadow-sm border-slate-300 bg-white overflow-hidden'>
        <div className='flex items-center justify-between gap-2 px-3 py-3 border-b border-slate-300 bg-slate-50'>
          <span className='text-2xl font-bold text-slate-900'>{title}</span>
          <span className='text-xl text-slate-700 whitespace-nowrap'>
            총 <strong className='text-2xl text-slate-900'>{total ?? 0}</strong> 건
          </span>
        </div>

        {loadingGate && !loading ? (
          <div className='flex justify-center py-8'>
            <CircularProgress size={32} />
          </div>
        ) : (
          <div className='flex flex-col'>
            {_.map(legend, (item, i) => (
              <div
                key={i}
                className={`flex items-center gap-3 px-3 py-4 border-t border-slate-200 first:border-t-0 ${
                  interactive ? 'cursor-pointer active:bg-slate-100' : ''
                }`}
                onClick={interactive ? item.onClick : undefined}
              >
                <SquareIcon style={{color: item.color, fontSize: 24}} />
                <span className='text-xl text-slate-800 flex-1 min-w-0 truncate'>{item.label}</span>
                <span className='text-2xl font-bold text-slate-900 whitespace-nowrap'>
                  {item.count ?? 0} 건
                </span>
                {interactive && (
                  <ChevronRightIcon className='text-slate-600' style={{fontSize: 28}} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={`card card-body align-items-center col overflow-hidden ${cardPadding}`}
      style={{flex: '1 1 260px', minWidth: 0}}
    >
      <div
        className={'row gap-2 col justify-content-center align-items-center'}
        style={{minWidth: 0}}
      >
        {loadingGate && !loading ? (
          <span className='spinner-border align-middle ms-2 text-primary'></span>
        ) : (
          <>
            <div
              className={'align-self-center col cursor-pointer mx-auto'}
              id={chartId}
              style={{width: '100%', maxWidth: 300, minWidth: 0}}
            >
              {chartType === 'doughnut' ? (
                <Doughnut data={data} options={options} />
              ) : (
                <Bar data={data} options={options} />
              )}
            </div>
            <div className={'row gap-2 align-items-center'} id={legendId}>
              {_.map(legend, (item, i) => (
                <div
                  key={i}
                  className={'w-100 d-grid'}
                  style={{justifyItems: 'center', marginLeft: legendItemMarginLeft}}
                >
                  <div
                    className={`d-flex gap-2 justify-content-start fw-bold text-break ${legendFontClass}`}
                    style={{
                      cursor: 'pointer',
                      fontSize,
                      width: '100%',
                      maxWidth: legendItemWidth,
                      minWidth: 0,
                    }}
                    onClick={item.onClick}
                  >
                    <SquareIcon style={{color: item.color}} />
                    <span>
                      {item.label} ( {item.count} 건 )
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default StatusChartCard
